import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { AnyEvent, User } from '@shared/types.ts';
import { relTime } from '../../lib/format.ts';
import { summarise, summaryLabel } from '../../lib/breast.ts';
import { Icon } from '../Icon.tsx';

type Props = {
  items: AnyEvent[];
  usersById: Record<string, User>;
  onSelect?: (e: AnyEvent) => void;
};

export function RecentStrip({ items, usersById, onSelect }: Props) {
  const recent = items.slice(0, 4);
  // Keep "Xh ago" labels live without re-rendering the whole Home tree.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  if (!recent.length) return null;
  return (
    <div style={{ padding: '0 16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-3)',
            letterSpacing: 0.04,
            textTransform: 'uppercase',
          }}
        >
          Recent
        </div>
        <Link
          to="/events"
          style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 500, textDecoration: 'none' }}
        >
          All events →
        </Link>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {recent.map((e, i) => {
          const dot = dotFor(e);
          const t = e._t === 'diaper' ? e.occurred_at : e.started_at;
          const who = usersById[e.created_by];
          const radius =
            i === 0 ? '14px 14px 4px 4px' : i === recent.length - 1 ? '4px 4px 14px 14px' : '4px';
          return (
            <button
              type="button"
              key={e.id}
              onClick={() => onSelect?.(e)}
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                background: 'var(--bg-card)',
                borderRadius: radius,
                cursor: onSelect ? 'pointer' : 'default',
              }}
            >
              <span
                style={{ width: 10, height: 10, borderRadius: 999, background: dot.c, flexShrink: 0 }}
              />
              <span
                style={{
                  flex: 1,
                  fontSize: 14,
                  color: 'var(--text-1)',
                  fontWeight: 500,
                  letterSpacing: -0.005,
                  textTransform: 'capitalize',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dot.label}
                </span>
                {hasNotes(e) && (
                  <span style={{ color: 'var(--text-3)' }} title="Has a note">
                    <Icon.edit s={12} />
                  </span>
                )}
              </span>
              <span
                className="lt-tabular"
                style={{ fontSize: 12, color: 'var(--text-3)' }}
              >
                {fmtRecent(t)}
              </span>
              {who && (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    background: who.color ?? 'var(--accent)',
                    color: 'white',
                    fontSize: 9,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {who.name[0]?.toUpperCase()}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function dotFor(e: AnyEvent): { c: string; label: string } {
  if (e._t === 'sleep') return { c: 'var(--sleep-day)', label: e.location ? `sleep · ${e.location}` : 'sleep' };
  if (e._t === 'feed') {
    if (e.type === 'bottle')
      return { c: 'var(--bottle)', label: e.amount_ml ? `bottle ${e.amount_ml} ml` : 'bottle' };
    if (e.type === 'solid') return { c: 'var(--solid)', label: 'solid' };
    const s = summarise(e);
    if (s.hasHistory) {
      const sum = summaryLabel(s);
      return { c: 'var(--breast)', label: sum ? `breast · ${sum}` : 'breast' };
    }
    return { c: 'var(--breast)', label: e.type === 'breast_left' ? 'breast L' : 'breast R' };
  }
  if (e._t === 'pump') return { c: 'var(--pump)', label: e.yield_ml ? `pump ${e.yield_ml} ml` : 'pump' };
  return { c: e.kind === 'pee' ? 'var(--pee)' : 'var(--poop)', label: `diaper · ${e.kind}` };
}

function hasNotes(e: AnyEvent): boolean {
  const n = (e as { notes?: string | null }).notes;
  return typeof n === 'string' && n.trim().length > 0;
}

// Within 24h, show rounded "Xm ago" / "Xh ago". Older items show "yest 22:14"
// or "Mon 14:30" so the reader doesn't need to count days back.
function fmtRecent(sec: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - sec;
  if (diff < 86400) return relTime(sec);
  const d = new Date(sec * 1000);
  const clock = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const daysAgo = Math.floor((startToday.getTime() / 1000 - new Date(sec * 1000).setHours(0, 0, 0, 0) / 1000) / 86400);
  if (daysAgo === 1) return `yest ${clock}`;
  if (daysAgo < 7) return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]} ${clock}`;
  return `${d.getDate()} ${d.toLocaleString(undefined, { month: 'short' })} ${clock}`;
}
